import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CreditCard } from "lucide-react";
import { supabase } from "../../supabaseClient";

export default function ProgramUpgrade() {
  const { programId } = useParams();
  const [searchParams] = useSearchParams();
  const defaultProductId = process.env.REACT_APP_STRIPE_PROGRAM_PRODUCT_ID || "prod_U7E6APAzKhFQhJ";
  const [productId] = useState(searchParams.get("productId") || defaultProductId);
  const [prices, setPrices] = useState([]);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPriceId, setLoadingPriceId] = useState(null);
  const [currentPurchase, setCurrentPurchase] = useState(null);

  const normalizeText = (value) => String(value || "").trim().toLowerCase();

  const rosterValue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  useEffect(() => {
    const fetchProgram = async () => {
      if (!programId) return;
      const { data } = await supabase
        .from("programs")
        .select("id, name, sport, program_type")
        .eq("id", programId)
        .maybeSingle();
      setProgram(data || null);
    };

    fetchProgram();
  }, [programId]);

  useEffect(() => {
    const fetchCurrentPurchase = async () => {
      if (!programId) return;
      const today = new Date().toISOString().slice(0, 10);
      const { data, error: purchaseError } = await supabase
        .from("program_purchases")
        .select("*")
        .eq("program_id", programId)
        .lte("start_date", today)
        .gte("end_date", today)
        .order("end_date", { ascending: false })
        .limit(1);

      if (purchaseError) {
        console.error("Purchase lookup failed:", purchaseError);
        setCurrentPurchase(null);
        return;
      }

      const active = (data || []).find((row) => !row.status || row.status === "active") || null;
      setCurrentPurchase(active);
    };

    fetchCurrentPurchase();
  }, [programId]);

  useEffect(() => {
    const fetchPrices = async () => {
      if (!productId) return;
      setPricesLoading(true);

      const { data: authData } = await supabase.auth.getSession();
      const accessToken = authData?.session?.access_token;

      const { data, error: functionError } = await supabase.functions.invoke(
        "program-upgrade-prices",
        {
          body: { productId },
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        }
      );

      if (functionError) {
        console.error("Unable to load pricing tiers:", functionError);
        setPrices([]);
      } else {
        const list = (data?.prices || []).filter((p) => p?.unit_amount);
        list.sort((a, b) => (a.unit_amount || 0) - (b.unit_amount || 0));
        setPrices(list);
      }

      setPricesLoading(false);
    };

    fetchPrices();
  }, [productId]);

  const handleCheckout = async (price) => {
    if (!productId || !price?.id) return;

    setLoading(true);
    setLoadingPriceId(price.id);

    const successUrl = `${window.location.origin}/admin/program/${programId}/upgrade?status=success`;
    const cancelUrl = `${window.location.origin}/admin/program/${programId}/upgrade?status=cancel`;

    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;

      const { data, error: functionError } = await supabase.functions.invoke(
        "program-upgrade-checkout",
        {
          body: {
            productId,
            priceId: price.id,
            programId,
            userId: user?.id,
            customerEmail: user?.email,
            successUrl,
            cancelUrl,
            tier: price.metadata?.tier || price.nickname || price.product_name || "",
            rosterLimit: price.metadata?.roster_max || price.metadata?.roster_limit || "",
            rosterMin: price.metadata?.roster_min || "",
            rosterMax: price.metadata?.roster_max || price.metadata?.roster_limit || "",
            seasonPhase: price.metadata?.season_phase || "offseason",
          },
        }
      );

      if (functionError) throw functionError;
      if (data?.url) {
        window.location.href = data.url;
        return;
      }

      throw new Error("Checkout session failed to start.");
    } catch (err) {
      console.error("Upgrade checkout error:", err);
      alert(err.message || "Unable to start checkout.");
    } finally {
      setLoading(false);
      setLoadingPriceId(null);
    }
  };

  const showSuccess = searchParams.get("status") === "success";
  const showCancel = searchParams.get("status") === "cancel";

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center">
            <CreditCard size={20} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Upgrade Program Plan</h1>
            <p className="text-sm text-slate-500">Secure checkout powered by Stripe</p>
          </div>
        </div>

        <div className="mb-6">
          <p className="text-sm text-slate-600">Program</p>
          <p className="text-base font-semibold text-slate-900">
            {program?.name || "Program"} {program?.sport ? `- ${program.sport}` : ""}
          </p>
          {program?.program_type && (
            <p className="text-xs text-slate-500">{program.program_type}</p>
          )}
        </div>

        {showSuccess && (
          <div className="mb-6 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg p-3">
            Payment completed. Your subscription is being processed.
          </div>
        )}

        {showCancel && (
          <div className="mb-6 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3">
            Checkout canceled. You can try again anytime.
          </div>
        )}

        {currentPurchase && (
          <div className="mb-6 rounded-xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-800">
            Current plan: <span className="font-semibold">{currentPurchase.tier || "Active"}</span>
            {currentPurchase.roster_limit ? ` • Up to ${currentPurchase.roster_limit} athletes` : ""}
          </div>
        )}

        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
            Select Tier
          </label>
          {pricesLoading && (
            <div className="text-sm text-slate-500">Loading tiers...</div>
          )}
          {!pricesLoading && prices.length === 0 && (
            <div className="text-sm text-slate-500">No active prices found for this product.</div>
          )}
          {!pricesLoading && prices.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="rounded-2xl border border-slate-200 p-5 bg-white text-left">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Free</p>
                    <p className="text-xs text-slate-400 mt-1">All programs</p>
                  </div>
                  <div className="px-3 py-1 rounded-full text-[10px] font-bold uppercase bg-slate-900 text-white">
                    Included
                  </div>
                </div>
                <div className="flex items-end gap-1 mb-4">
                  <span className="text-3xl font-black text-slate-900">$0</span>
                  <span className="text-xs text-slate-500 pb-1">/ year</span>
                </div>
                <p className="text-xs text-slate-500 mb-6">
                  Every rostered athlete pays for their own offseason development plan.
                </p>
                <div className="text-xs text-slate-400 uppercase tracking-wide font-bold">Current base plan</div>
              </div>

              {prices.map((price) => {
                const amount = price.unit_amount ? (price.unit_amount / 100).toFixed(2) : "0.00";
                const interval = price.recurring?.interval || "year";
                const tier = price.metadata?.tier || price.nickname || price.product_name || "Program tier";
                const min = price.metadata?.roster_min || "";
                const max = price.metadata?.roster_max || price.metadata?.roster_limit || "";
                const rosterRange = min && max ? `${min} - ${max}` : max ? `Up to ${max}` : "Roster range";
                const currentTierMatch = (() => {
                  if (!currentPurchase) return false;
                  const tierMatch = normalizeText(currentPurchase.tier) === normalizeText(tier);
                  if (tierMatch) return true;
                  const rosterLimit = rosterValue(currentPurchase.roster_limit);
                  const minValue = rosterValue(min);
                  const maxValue = rosterValue(max);
                  if (rosterLimit === null) return false;
                  if (minValue !== null && maxValue !== null) {
                    return rosterLimit >= minValue && rosterLimit <= maxValue;
                  }
                  if (maxValue !== null) return rosterLimit === maxValue;
                  return false;
                })();

                return (
                  <div
                    key={price.id}
                    className={`rounded-2xl border p-5 text-left transition ${
                      currentTierMatch
                        ? "border-emerald-500 bg-emerald-50 shadow-lg shadow-emerald-100"
                        : "border-slate-200 hover:border-blue-300 hover:shadow-md"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide">{tier}</p>
                        <p className="text-xs text-slate-400 mt-1">{rosterRange} athletes</p>
                      </div>
                      {currentTierMatch ? (
                        <div className="px-3 py-1 rounded-full text-[10px] font-bold uppercase bg-emerald-600 text-white">
                          Current
                        </div>
                      ) : null}
                    </div>

                    <div className="flex items-end gap-1 mb-4">
                      <span className="text-3xl font-black text-slate-900">${amount}</span>
                      <span className="text-xs text-slate-500 pb-1">/ {interval}</span>
                    </div>

                    <p className="text-xs text-slate-500 mb-4">
                      Every rostered athlete pays for their own offseason development plan.
                    </p>

                    <p className="text-[11px] text-slate-400 uppercase tracking-wide font-bold mb-3">
                      Upgrade to this tier
                    </p>

                    <button
                      type="button"
                      onClick={() => handleCheckout(price)}
                      disabled={loading && loadingPriceId === price.id}
                      className="w-full px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                    >
                      {loading && loadingPriceId === price.id ? "Starting checkout..." : "Upgrade"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
